import {
  type Form,
  type FormRule,
  OverrideEntityType,
} from '@wix/ambassador-forms-v4-form/types';
import { type ProductSubmission } from './submission';
import { type ConditionJson, type RuleJson } from './types';

export function asRequired<T>(value: T | null | undefined): T {
  if (typeof asOptional(value) !== 'undefined') {
    return value as T;
  }
  throw new Error('Value is null | undefined');
}

export function isCondition(data: any): data is ConditionJson {
  return !!data?.fact && !!data?.operator;
}

export function getRulesOrConditions(
  data?: RuleJson | null,
): (RuleJson | ConditionJson)[] {
  return data?.and || data?.or || [];
}

export function asOptional<T>(value: T | null | undefined): T | undefined {
  return value == null ? undefined : value;
}

export function valueIsMissing<T>(value: T | null | undefined) {
  return (
    typeof asOptional(value) === 'undefined' ||
    (typeof value === 'string' && value === '') ||
    (Array.isArray(value) && !value.length)
  );
}

export function lessThan(
  given: number,
  expected: number,
  orEqual: boolean = false,
): boolean {
  return orEqual ? given <= expected : given < expected;
}

export function greaterThan(
  given: number,
  expected: number,
  orEqual: boolean = false,
): boolean {
  return orEqual ? given >= expected : given > expected;
}

export function between(
  given: number,
  lowerBound: number,
  upperBound: number,
): boolean {
  return greaterThan(given, lowerBound) && lessThan(given, upperBound);
}

function isTimeOnly(value: string): boolean {
  const timeOnlyRegex = /^\d{2}:\d{2}(:\d{2})?$/;
  return timeOnlyRegex.test(value);
}

export function asUtcDate(value: string): Date | null {
  try {
    const prefixAnyDate = '2000-01-01 ';
    const date = isTimeOnly(value)
      ? new Date(prefixAnyDate + value)
      : new Date(value);

    return isNaN(date.getTime()) ? null : date;
  } catch (_) {
    return null;
  }
}

export function getNumberValuesToCompare(...values: any[]): (number | null)[] {
  return values.map((value) => {
    if (typeof value === 'string') {
      return getDateUtcTimestamp(value);
    }
    if (typeof value === 'number') {
      return value;
    }
    if (isProductSubmission(value)) {
      return getProductPrice(value);
    }
    return null;
  });
}

function getDateUtcTimestamp(givenValue: string): number | null {
  const givenDate = asUtcDate(givenValue);
  return givenDate ? givenDate.getTime() : null;
}

export function getProductPrice(givenValue: ProductSubmission[]) {
  return givenValue[0].price;
}

export function isObject(givenValue: any): givenValue is object {
  return (
    typeof givenValue === 'object' &&
    !Array.isArray(givenValue) &&
    givenValue !== null
  );
}

export const isArrayOfNumbers = (value: any[]): value is number[] => {
  return value.every((item) => typeof item === 'number');
};

export function isProductSubmission(value: any): value is ProductSubmission[] {
  return (
    Array.isArray(value) &&
    value.every(
      (element) =>
        isObject(element) &&
        'productId' in element &&
        'price' in element &&
        'quantity' in element,
    )
  );
}

export function isValueEmpty(givenValue: any) {
  return givenValue === undefined || givenValue === null || givenValue === '';
}

function isValidCondition(
  rule: RuleJson | undefined | null,
  fields: { id?: string }[] = [],
): boolean {
  if (rule === undefined) {
    return true;
  }
  const rulesOrConditions = getRulesOrConditions(rule);
  if (!rulesOrConditions.length) {
    return false;
  }
  return rulesOrConditions.every((conditionOrRule) => {
    if (isCondition(conditionOrRule)) {
      return fields?.some((field) => field.id === conditionOrRule.fact);
    }
    return isValidCondition(conditionOrRule, fields);
  });
}

export function isFormRuleValid(
  formRule: FormRule | undefined | null,
  formSchema?: Form,
): boolean {
  if (!formRule || !formRule?.overrides?.length || !formSchema) {
    return false;
  }
  const { fields, nestedForms, formFields } = formSchema;
  const fieldsToCheck = (fields ?? []).concat(formFields ?? []);
  const conditionValid = isValidCondition(formRule.condition, fieldsToCheck);

  const overrideValid = formRule?.overrides.some((override) => {
    if (override.entityType === OverrideEntityType.FORM) {
      return (
        formSchema.id === override.entityId ||
        nestedForms?.some(
          (nestedForm) => nestedForm.form?.id === override.entityId,
        )
      );
    }

    const wasFieldFound = (fieldsToCheck?.map((f) => f.id) ?? []).includes(
      override.entityId!!,
    );
    return override.entityId ? wasFieldFound : false;
  });

  return conditionValid && overrideValid;
}
